import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as artifactService from '../../services/artifactService';
import { DiffViewer } from './DiffViewer';
import { ImageDiffViewer } from './ImageDiffViewer';
import { ArtifactType } from '../../types/artifact';
import { useTheme } from '../../contexts/ThemeContext';

interface ArtifactVersionHistoryProps {
  artifactId: string;
  artifactType?: ArtifactType;
}

export const ArtifactVersionHistory: React.FC<ArtifactVersionHistoryProps> = ({
  artifactId,
  artifactType
}) => {
  const { darkMode } = useTheme();
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
  const [compareMode, setCompareMode] = useState(false);
  const queryClient = useQueryClient();
  
  const { data: versions, isLoading, error } = useQuery(
    ['artifactVersions', artifactId],
    () => artifactService.getArtifactVersions(artifactId)
  );
  
  const restoreMutation = useMutation(
    (versionId: string) => artifactService.restoreArtifactVersion(artifactId, versionId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['artifact', artifactId]);
        queryClient.invalidateQueries(['artifactVersions', artifactId]);
      }
    }
  );
  
  const handleVersionSelect = (versionId: string) => {
    if (compareMode) {
      if (selectedVersions.includes(versionId)) {
        setSelectedVersions(selectedVersions.filter(id => id !== versionId));
      } else if (selectedVersions.length < 2) {
        setSelectedVersions([...selectedVersions, versionId]);
      }
    } else {
      setSelectedVersions([versionId]);
    }
  };
  
  const handleRestore = (versionId: string) => {
    restoreMutation.mutate(versionId);
  };
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };
  
  const renderDiffViewer = () => {
    if (!artifactType || selectedVersions.length !== 2) return null;
    
    // For image and diagram artifacts, use the ImageDiffViewer
    if (artifactType === ArtifactType.IMAGE || artifactType === ArtifactType.DIAGRAM) {
      return (
        <ImageDiffViewer 
          artifactId={artifactId} 
          versionA={selectedVersions[0]} 
          versionB={selectedVersions[1]} 
        />
      );
    }
    
    // For text-based artifacts, use the regular DiffViewer
    return (
      <DiffViewer 
        artifactId={artifactId} 
        versionA={selectedVersions[0]} 
        versionB={selectedVersions[1]} 
      />
    );
  };
  
  if (isLoading) {
    return <div className={`text-center py-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Loading version history...</div>;
  }

  if (error) {
    return (
      <div className={`text-center py-4 rounded-lg p-4 ${
        darkMode ? 'bg-red-900/20 border border-red-700 text-red-400' : 'text-red-600'
      }`}>
        Error loading version history
      </div>
    );
  }

  if (!versions || versions.length === 0) {
    return <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No version history available</div>;
  }

  return (
    <div className={`artifact-version-history rounded-lg overflow-hidden border ${
      darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white shadow border-transparent'
    }`}>
      <div className={`p-4 border-b flex justify-between items-center ${
        darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
      }`}>
        <h3 className={`font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>Version History</h3>
        <div className="flex items-center">
          <span className={`mr-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Compare</span>
          <button
            type="button"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              compareMode ? 'bg-blue-600' : darkMode ? 'bg-gray-600' : 'bg-gray-200'
            }`}
            onClick={() => {
              setCompareMode(!compareMode);
              setSelectedVersions([]);
            }}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                compareMode ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {compareMode && selectedVersions.length === 2 && (
        <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {renderDiffViewer()}
        </div>
      )}

      <div className="overflow-auto max-h-96">
        {versions.map(version => (
          <div
            key={version.id}
            className={`p-4 border-b last:border-b-0 cursor-pointer transition-colors ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            } ${
              selectedVersions.includes(version.id)
                ? darkMode
                  ? 'bg-blue-900/30 border-l-2 border-l-blue-500'
                  : 'bg-blue-50 border-l-2 border-l-blue-500'
                : darkMode
                  ? 'hover:bg-gray-700'
                  : 'hover:bg-gray-50'
            }`}
            onClick={() => handleVersionSelect(version.id)}
          >
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-baseline">
                  <span className={`font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                    v{version.version_number}
                  </span>
                  <span className={`ml-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {formatDate(version.created_at)}
                  </span>
                </div>
                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  By {version.agent_name}
                </p>
                {version.change_description && (
                  <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {version.change_description}
                  </p>
                )}
              </div>
              <div>
                {!compareMode && (
                  <button
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRestore(version.id);
                    }}
                  >
                    Restore
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
